import { isDmaUser } from '@upright/common/utils';
import { useGetLoggedInUser, useIsTeam } from '@upright/ui/common/generic/hooks';
import { DesktopOnly, TabletOrNarrowerOnly } from '@upright/ui/common/generic/layout/breakpoint-utils';
import { useMemo } from 'react';
import { Route, Routes, useLocation } from 'react-router-dom';
import { isAccessProfilePath, isUnifiedEntityFramePath } from '../../unified-shell/company-profile-route';
import { BouToolHeader } from '../../../common/generic/components/bou-tool-header';
import { MobileHeader } from '../../../common/generic/components/bou-tool-header/mobile-header';
import { ChatHeader } from '../../internal/chat-view/chat-header';
import { UnifiedTopBarOverLegacyFrame } from '../../unified-shell/unified-top-bar-over-legacy';
import { useUnifiedShell } from '../../unified-shell/use-unified-shell';
import { useUnifiedShellFrameActive } from '../../unified-shell/use-unified-shell-frame-active';
import { useUnifiedShellFrameClaimed } from '../../unified-shell/unified-shell-frame-claim';

export function HeaderMaybe() {
  return (
    <Routes>
      <Route path="/share/:entityId" element={null} />
      <Route path="/render/*" element={null} />
      {/*
       * One prototype, no header: a prototype proposing its own top navigation
       * cannot be reviewed underneath ours, and the harness gives it the whole
       * viewport (`withProtoFrame`). The single-segment path is deliberate — the
       * `/internal/proto` index is an ordinary internal page and keeps its header.
       */}
      <Route path="/internal/proto/:protoId" element={null} />
      <Route path="/magic" element={null} />
      {/* The API reference carries its own minimal bar (`views/api-docs/api-docs-top-bar.tsx`). */}
      <Route path="/docs/*" element={null} />
      <Route path="/dashboard/*" element={null} />
      {/* `/go/…` campaign resolvers only ever show a spinner before redirecting;
          a header would flash in and out for no purpose. */}
      <Route path="/go/*" element={null} />
      <Route path="/company/:companyId/:companyName/dashboard/*" element={null} />
      {/* `/chat` and a conversation under it (PLT-6610) — one header decision for both. */}
      <Route path="/chat/*" element={<ChatHeaderForRoute />} />
      <Route path="/*" element={<InvestorSideHeaderForRoute />} />
    </Routes>
  );
}

/**
 * Everything not claimed above: the investor home, the public database, the entity views,
 * the internal views. Three things can render here.
 *
 * While the unified shell's frame is up, nothing — that frame carries its own bar the way the
 * corporate shell does (`views/unified-shell/`). That includes a frame a route renders for itself:
 * a unified profile, found by its path, and "Page not found", which claims it
 * (`unified-shell-frame-claim.ts`) because an unmatched path has nothing to test. On every other route, an opted-in user gets the
 * unified shell's white bar over whatever page frame the route has, and everyone else the dark
 * `BouToolHeader`, as before.
 *
 * The two conditions are deliberately different (PLT-6767). Which *bar* a user gets follows their
 * shell opt-in alone, so it is the same bar everywhere; which *frame* a route gets still follows
 * `useUnifiedShellFrameActive`, so the views the shell has not reached keep `BouPageWithFrame`
 * until PLT-6734 and its siblings move them. Reading the frame's answer for both is what left an
 * opted-in investor meeting the dark bar one click off the home views.
 *
 * Below tablet the opted-in user keeps the legacy `MobileHeader`. The legacy frame hides its rail
 * entirely at that width, so that header's burger menu is the only navigation those pages have, and
 * the unified bar has nothing to replace it with until PLT-6738 lands a mobile design for this
 * shell. The breakpoint split itself is `BouToolHeader`'s own, so this is the seam readers already
 * meet rather than a new one.
 *
 * Nothing renders while a customer's opt-in is still being resolved, so the dark bar is not painted
 * and then swapped for the white one a moment later. `frame.loading` does not cover that on the
 * routes the frame declines: it answers for the frame, and reports no wait at all once the route is
 * one the frame does not claim.
 */
function InvestorSideHeaderForRoute() {
  const frame = useUnifiedShellFrameActive();
  const shell = useUnifiedShell();
  const { pathname } = useLocation();
  const isTeam = useIsTeam();
  // An entity profile or its impact drilldown brings the frame, and with it the bar, itself.
  // Only the team has the access route; for anyone else the path matches nothing (`frameClaimed`).
  const ownsFrame = isUnifiedEntityFramePath(pathname) || (isTeam && isAccessProfilePath(pathname));
  // A route rendering its own frame where no path test can say so, such as "Page not found".
  const frameClaimed = useUnifiedShellFrameClaimed();
  if (frame.active || frame.loading || shell.loading || frameClaimed || (shell.enabled && ownsFrame)) {
    return null;
  }
  if (shell.enabled) {
    return (
      <div>
        <DesktopOnly>
          <UnifiedTopBarOverLegacyFrame />
        </DesktopOnly>
        <TabletOrNarrowerOnly>
          <MobileHeader />
        </TabletOrNarrowerOnly>
      </div>
    );
  }
  return <BouToolHeader />;
}

/**
 * On `/chat`, the header is the minimal `ChatHeader` (logo + logout-only) — unless the unified
 * shell's frame is up, where chat is a view inside that shell and the frame carries the bar, the
 * same as on every other route it wraps (PLT-6610). A minimal chat header above the shell's own
 * would be two bars, and its back-to-where-you-came-from logo answers a question the rail beside
 * the page already answers.
 *
 * The header itself is always the light (white) variant — the chat page is
 * its own polished surface and we no longer try to chrome-match the side
 * the user came from. What still tracks the entry point is *where the logo
 * goes*: the logo always takes the user back to whichever side they were
 * on before opening chat, so plain-clicking the logo is a true "back"
 * affordance.
 *
 * Entry-point sources, in priority order:
 * - `?from=dma`  → DMA dashboard (`/dashboard`).
 * - `?from=public` → public-side root (`/`).
 * - Fallback when the param is missing (direct visit, refresh after the
 *   `ChatView` strips the URL, bookmark): infer from org type — DMA orgs
 *   land on `/dashboard`, everyone else lands on `/`.
 *
 * The param is captured once on mount via `useMemo`, so it survives the
 * sibling `ChatView` stripping it from the URL.
 */
function ChatHeaderForRoute() {
  const frame = useUnifiedShellFrameActive();
  const user = useGetLoggedInUser();
  const entryPoint = useMemo<'dma' | 'public' | null>(() => {
    if (typeof window === 'undefined') return null;
    const raw = new URLSearchParams(window.location.search).get('from');
    if (raw === 'dma' || raw === 'public') return raw;
    return null;
  }, []);

  // Whether the user came from the DMA side (explicit hint or org-type
  // fallback). Drives only the logo destination — the header chrome is
  // always light.
  const cameFromDma = entryPoint === 'dma' || (entryPoint === null && !!user && isDmaUser(user));
  const logoHref = cameFromDma ? '/dashboard' : '/';

  if (frame.active || frame.loading) {
    return null;
  }
  return <ChatHeader variant="light" logoHref={logoHref} />;
}
